import {isHandledPaymentInstruction} from '@sollapay/domain/rules';
import {
  PAYMENT_INSTRUCTION_STATUS_GROUP,
  PaymentInstructionStatusGroup,
  PaymentMethod
} from '@sollapay/enums';
import {Table, TableActionsCell, Tooltip, Typography, nodeColumn} from '@sollapay/ui/components';
import {
  formatRelativeTimeLocalized,
  formatDateLocalized,
  formatDateTimeLocalized,
  parseMoney
} from '@sollapay/utils';
import {PaymentMethodBadge, ReferenceCodeBadge} from '@sollapay/web-core';
import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {AmountCell} from '@/components';
import {
  BuyerNameLink,
  PaymentInstructionStatusBadge,
  TransferTypeCell
} from '@/features/buyers/components';
import {PAYMENT_METHOD_BADGE_I18N} from '@/features/buyers/config/buyers.config';
import {
  canCancelInstruction,
  canMarkHandledInstruction,
  canResendInstruction
} from '@/features/buyers/utils';
import {PaymentInstructionMoreMenu} from '@/features/buyers/view/components';

import {HandledGroupHeader} from './HandledGroupHeader';
import {HandledLabel} from './HandledLabel';
import {MarkHandledButton} from './MarkHandledButton';

import type {PaymentInstructionMenuAction} from '@/features/buyers/view/components';
import type {BuyerDTO, PaymentInstructionDTO} from '@sollapay/types';
import type {FC, ReactNode} from 'react';

export interface InstructionsTableProps {
  instructions: PaymentInstructionDTO[];
  buyersById: Map<string, BuyerDTO>;
  assetNameByPurchaseId: Map<string, string>;
  showAssetColumn?: boolean;
  emptyState?: ReactNode;
  onResend?: (instruction: PaymentInstructionDTO) => void;
  onCancel?: (instruction: PaymentInstructionDTO) => void;
  onSetHandled?: (instruction: PaymentInstructionDTO, handled: boolean) => void;
  groupHandled?: boolean;
  onViewBuyer?: (buyerId: string, trustBuyerPurchaseId: string) => void;
}

/**
 * Account-wide instructions list — a higher-level view than the single-buyer payment history
 * table (`BuyerPaymentHistoryTable`), not a copy of it: always shows the Buyer column (there's no
 * single-buyer case here), adds Asset, drops Percent, and surfaces link expiry as a tooltip on the
 * payment method badge while a Payment Initiation request is still In Progress.
 */
export const InstructionsTable: FC<InstructionsTableProps> = ({
  instructions,
  buyersById,
  assetNameByPurchaseId,
  showAssetColumn = true,
  emptyState,
  onResend,
  onCancel,
  onSetHandled,
  groupHandled = false,
  onViewBuyer
}) => {
  const {t, i18n} = useTranslation(['trustAccounts', 'common']);
  const [isHandledExpanded, setIsHandledExpanded] = useState(false);
  const hasHandledRows = useMemo(
    () => instructions.some(isHandledPaymentInstruction),
    [instructions]
  );
  const canMarkHandled = groupHandled && !!onSetHandled;

  const columns = useMemo(
    () => [
      nodeColumn<PaymentInstructionDTO>({
        id: 'buyer',
        header: t('trustAccounts:view.instructions.columns.buyer'),
        width: 'min',
        maxWidth: 200,
        accessorFn: row => buyersById.get(row.buyerPartyId ?? '')?.fullName ?? '',
        cell: row => {
          const buyer = buyersById.get(row.buyerPartyId ?? '');
          if (!buyer || !row.buyerPartyId) {
            return (
              <Typography size="sm" color="tertiary">
                {t('trustAccounts:view.instructions.filter.unassignedBuyer')}
              </Typography>
            );
          }
          const buyerPartyId = row.buyerPartyId;
          return (
            <BuyerNameLink
              buyer={buyer}
              onClick={() => onViewBuyer?.(buyerPartyId, row.trustBuyerPurchaseId)}
            />
          );
        }
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'status',
        header: t('trustAccounts:view.instructions.columns.status'),
        width: 'min',
        maxWidth: 180,
        accessorFn: row => row.status,
        cell: row => <PaymentInstructionStatusBadge instruction={row} />
      }),
      ...(showAssetColumn
        ? [
            nodeColumn<PaymentInstructionDTO>({
              id: 'asset',
              header: t('trustAccounts:view.instructions.columns.asset'),
              width: 'min',
              maxWidth: 200,
              accessorFn: row => assetNameByPurchaseId.get(row.trustBuyerPurchaseId) ?? '',
              cell: row => (
                <Typography size="sm" className="truncate">
                  {assetNameByPurchaseId.get(row.trustBuyerPurchaseId) ?? '—'}
                </Typography>
              )
            })
          ]
        : []),
      nodeColumn<PaymentInstructionDTO>({
        id: 'amount',
        header: t('trustAccounts:view.instructions.columns.amount'),
        width: 'min',
        maxWidth: 140,
        accessorFn: row => parseMoney(row.amount).toNumber(),
        cell: row => <AmountCell amount={row.amount} />
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'paymentMethod',
        header: t('trustAccounts:view.instructions.columns.paymentMethod'),
        width: 'min',
        maxWidth: 180,
        accessorFn: row => row.initiationMethod,
        cell: row => {
          const isLinkActive =
            row.initiationMethod !== PaymentMethod.MANUAL &&
            PAYMENT_INSTRUCTION_STATUS_GROUP[row.status] ===
              PaymentInstructionStatusGroup.IN_PROGRESS;
          return (
            <Tooltip
              content={
                isLinkActive && row.linkExpiry
                  ? t('trustAccounts:view.instructions.linkExpiryTooltip', {
                      expiresAt: formatDateTimeLocalized(row.linkExpiry.expiresAt, i18n.language)
                    })
                  : undefined
              }
            >
              <PaymentMethodBadge method={row.initiationMethod} {...PAYMENT_METHOD_BADGE_I18N} />
            </Tooltip>
          );
        }
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'transferType',
        header: t('trustAccounts:view.instructions.columns.transferType'),
        width: 'min',
        accessorFn: row => row.transferType ?? '',
        cell: row => <TransferTypeCell transferType={row.transferType} />
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'reference',
        header: t('trustAccounts:view.instructions.columns.reference'),
        width: 'min',
        maxWidth: 160,
        accessorFn: row => row.referenceCode,
        cell: row => (
          <ReferenceCodeBadge
            code={row.referenceCode}
            fieldName={t('common:fields.referenceCode.label')}
          />
        )
      }),
      nodeColumn<PaymentInstructionDTO>({
        id: 'createdAt',
        header: t('trustAccounts:view.instructions.columns.createdAt'),
        width: 'min',
        maxWidth: 140,
        accessorFn: row => row.createdAt,
        cell: row => (
          <Typography
            size="sm"
            className="tabular-nums"
            title={formatDateLocalized(row.createdAt, i18n.language)}
          >
            {formatRelativeTimeLocalized(row.createdAt, i18n.language)}
          </Typography>
        )
      }),
      ...(hasHandledRows || canMarkHandled
        ? [
            nodeColumn<PaymentInstructionDTO>({
              id: 'handled',
              header: '',
              width: 'min',
              maxWidth: 200,
              enableSorting: false,
              accessorFn: row => row.handledAt ?? '',
              cell: row => {
                if (row.handledAt && isHandledPaymentInstruction(row)) {
                  return (
                    <HandledLabel handledAt={row.handledAt} handledByName={row.handledByName} />
                  );
                }
                if (!canMarkHandled || !canMarkHandledInstruction(row)) return null;
                return (
                  <div className="flex justify-start opacity-0 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100">
                    <MarkHandledButton
                      onClick={() => {
                        setIsHandledExpanded(true);
                        onSetHandled?.(row, true);
                      }}
                    />
                  </div>
                );
              }
            })
          ]
        : []),
      ...(onResend || onCancel || onSetHandled
        ? [
            nodeColumn<PaymentInstructionDTO>({
              id: 'actions',
              header: '',
              width: 'min',
              maxWidth: 52,
              enableSorting: false,
              accessorFn: row => row.id,
              cell: row => {
                const actions: PaymentInstructionMenuAction[] = [];
                if (onResend && canResendInstruction(row)) actions.push('resend');
                if (onCancel && canCancelInstruction(row)) actions.push('cancel');
                if (onSetHandled && isHandledPaymentInstruction(row)) actions.push('unmarkHandled');
                if (actions.length === 0) return null;
                return (
                  <TableActionsCell>
                    <PaymentInstructionMoreMenu
                      actions={actions}
                      onResend={onResend ? () => onResend(row) : undefined}
                      onCancel={onCancel ? () => onCancel(row) : undefined}
                      onUnmarkHandled={onSetHandled ? () => onSetHandled(row, false) : undefined}
                      orientation="horizontal"
                    />
                  </TableActionsCell>
                );
              }
            })
          ]
        : [])
    ],
    [
      assetNameByPurchaseId,
      showAssetColumn,
      buyersById,
      canMarkHandled,
      hasHandledRows,
      i18n.language,
      onCancel,
      onResend,
      onSetHandled,
      onViewBuyer,
      t
    ]
  );

  const sorted = useMemo(
    () =>
      [...instructions].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [instructions]
  );

  const handledGroupProps = useMemo(
    () =>
      groupHandled
        ? {
            isRowPinned: (row: PaymentInstructionDTO) => !isHandledPaymentInstruction(row),
            getGroupKey: (row: PaymentInstructionDTO) =>
              isHandledPaymentInstruction(row) ? 'handled' : 'open',
            isRowCollapsed: (row: PaymentInstructionDTO) =>
              !isHandledExpanded && isHandledPaymentInstruction(row),
            renderGroupHeader: (row: PaymentInstructionDTO, count: number) =>
              isHandledPaymentInstruction(row) ? (
                <HandledGroupHeader
                  count={count}
                  isExpanded={isHandledExpanded}
                  onToggle={() => setIsHandledExpanded(expanded => !expanded)}
                />
              ) : null,
            getRowClassName: () => 'group/row'
          }
        : {},
    [groupHandled, isHandledExpanded]
  );

  return <Table columns={columns} data={sorted} emptyState={emptyState} {...handledGroupProps} />;
};
